import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "styled-components";
import { Button } from "../../../components/common";
import { Text } from "../../../components/common/text/text";
import FilterForm from "../../../components/home-components/filter-form/filter-form";
import Header from "../../../components/home-components/header/header";
import Slider from "../../../components/home-components/slider";
import Table from "../../../components/home-components/table";
import { HeaderItem } from "../../../components/home-components/table/components/header";
import {
  DataCell,
  InfoTableContainer,
  TableRow,
} from "../../../components/home-components/table/index.styled";
import {
  getCandidateHireModel,
  getCandidateHireTypes,
  getCandidateSalary,
  getMainSkills,
} from "../../../components/home-components/table/utils/table.utils";
import Modal from "../../../components/modal/modal";
import Talent from "../../../components/talent/talent";
import { useModal, useQuery } from "../../../hooks";
import useCandidateStatus from "../../../hooks/use-candidate-status";
import useLocalStorage from "../../../hooks/use-local-storage";
import { Candidate } from "../../../models/candidate.model";
import HomeDashboardPageQuery from "../../../models/home-dashboard-page-query.model";
import { AppState } from "../../../store/app-state";
import {
  clearCandidateDetail,
  clearCandidates,
  fetchCandidatePool,
} from "../../../store/candidate/candidate-actions";
import {
  fetchEventData,
  fetchEventTracks,
  fetchTrackCandidates,
} from "../../../store/event/event-actions";
import {
  fetchSetInvisible,
  fetchSetVisible,
} from "../../../store/load/load-actions";
import { ViewDesktopFilters } from "../selected-candidates/selected-candidates-page.styled";
import {
  Buttons,
  Container,
  Content,
  TalentPoolContainer,
} from "./talent-pool-page.styled";
import { fetchCompanyPlan } from "../../../store/company/company-actions";
import { getCurrentPlan } from "../../../store/plan/plan-actions";
import useChat from "../../../hooks/use-chat";
import { useTourContext } from "../../../hooks/user-tour";
import { initialTour } from "../../../store/tours";
import EmptyState from "../../../components/home-components/empty-state";
import TooltipWarning from "../../../components/tooltip-warning";
import { queryLoaded } from "../../../utils/query.utils";
import { Events } from "../../../utils/constants";
import { useFilterContext } from "../../../contexts/filter";
import useUmamiTracking from "../../../hooks/use-umami-tracking";

const eventSlug: string = "talent-match";
const modalTalent = document.getElementById("modal-talent");

const TalentPoolPage: React.FC = () => {
  useChat();
  const { isShown, open, close } = useModal();
  const theme = useTheme();
  const { t } = useTranslation("private-home-page");
  const dispatch = useDispatch();
  const user = useSelector((state: AppState) => state.authState.user);
  const [query, setQuery] = useQuery<HomeDashboardPageQuery>();
  const { candidatePoolData } = useSelector(
    (state: AppState) => state.candidateState
  );
  const { currentEventTrackData } = useSelector(
    (state: AppState) => state.eventState
  );
  const [showModalOnboarding, setShowModalOnboarding] = useState(false);
  const [isJobOfferOpen, setIsJobOfferOpen] = useState<boolean>(false);
  const [onboarding, setOnboarding] = useLocalStorage("onboarding", false);
  const { handleSetStep } = useTourContext();
  const { filters: localFilter } = useFilterContext();
  const pushUmami = useUmamiTracking();

  useEffect(() => {
    dispatch(fetchEventData(eventSlug));
    dispatch(fetchEventTracks(eventSlug));
    dispatch(fetchCompanyPlan());
    dispatch(getCurrentPlan());
  }, [dispatch]);

  useEffect(() => {
    if (!candidatePoolData.loading) {
      dispatch(fetchSetInvisible());
    }
  }, [candidatePoolData.loading, dispatch]);

  const handleSearch = useCallback(
    (query: HomeDashboardPageQuery) => {
      const newFilters = { ...query };
      delete newFilters.tour;
      delete newFilters.onboarding;
      delete newFilters.forceTour;
      if (newFilters.ecosystem) {
        const lastFilters = { ...query };
        delete lastFilters.tour;
        delete lastFilters.onboarding;
        delete lastFilters.forceTour;
        if (query.ecosystem) {
          dispatch(
            fetchCandidatePool(query.ecosystem, { ...query, pageSize: 100 })
          );
          dispatch(fetchSetVisible());
        }
      }
      pushUmami(Events.SEARCH, {
        filters: localFilter,
        locale: 'ranking'
      }, user);
    },
    [dispatch, pushUmami, localFilter, user]
  );

  useEffect(() => {
    const loaded = queryLoaded(query);
    if (
      !candidatePoolData.data &&
      query.ecosystem &&
      !candidatePoolData.loading &&
      !candidatePoolData.error
    ) {
      handleSearch(query);
    } else if (!query.currency && user && loaded) {
      query.currency = user?.corporate?.currency || "BRL";
      setQuery(query);
    }
  }, [dispatch, query, candidatePoolData, handleSearch, setQuery, user]);

  useEffect(() => {
    if (
      query.onboarding &&
      query.onboarding === "true" &&
      !onboarding &&
      candidatePoolData.data
    ) {
      setShowModalOnboarding(true);
      setQuery({ ...query, onboarding: "false" });
    }
  }, [query, onboarding, setOnboarding, setQuery, candidatePoolData.data]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const ecoParam = Object.keys(q).length;
    if (ecoParam === 0) {
      dispatch(clearCandidates());
    }
  }, [dispatch]);

  const tableHeaderItems: HeaderItem[] = [
    { value: "position", width: "2%" },
    { value: "name", width: "2%" },
    { value: "main-skills", width: "20%" },
    { value: "experience", width: "10%" },
    { value: "wage-claim", width: "15%" },
    { value: "work-model", width: "15%" },
    { value: "hire-types", width: "15%" },
  ];

  const handleOpen = useCallback(() => {
    open();
  }, [open]);

  const handleClose = () => {
    if (currentEventTrackData.data?.register) {
      dispatch(fetchTrackCandidates(currentEventTrackData.data.uuid, query));
    }
    dispatch(clearCandidateDetail());
    close();
  };

  const { candidateDetailData } = useSelector(
    (state: AppState) => state.candidateState
  );

  const [index, setIndex] = useState<number>(-1);

  const candidate = useMemo((): Candidate | undefined => {
    return index >= 0 ? candidatePoolData.data?.[index] : undefined;
  }, [candidatePoolData, index]);

  const candidateStatus = useCandidateStatus({
    candidateId: candidate?.id || "",
  });

  const getOriginalWageClaim = useCallback(
    (candidate: Candidate) => {
      return candidate?.wage_claims?.[0]?.brl
        ? t("wage-claim-converted-original", {
            originalValue: parseFloat(
              candidate.wage_claims[0].brl
            ).toLocaleString(),
          })
        : t("wage-claim-converted");
    },
    [t]
  );

  const handleModal = useCallback(
    (candidate: Candidate, candidateStatus) => {
      (!candidateStatus?.hired ||
        candidateStatus?.hired === user?.corporate.id ||
        candidate.company_hired === user?.corporate.id) &&
        handleOpen();
    },
    [handleOpen, user]
  );

  const closeOnboardingModal = useCallback(() => {
    setShowModalOnboarding(false);
    if (candidatePoolData.data && candidatePoolData.data.length > 0) {
      const action = () => {
        setIndex(0);
        candidatePoolData.data &&
          handleModal(candidatePoolData.data[0], candidateStatus);
      };

      const closeAction = () => {
        close();
      };

      handleSetStep(initialTour(action, t, closeAction));
      setOnboarding(true);
      let q = { ...query };
      delete q.forceTour;
      setQuery({ ...q, tour: "true" });
    }
  }, [
    candidatePoolData,
    setOnboarding,
    handleModal,
    handleSetStep,
    candidateStatus,
    setQuery,
    query,
    t,
    close,
  ]);

  useEffect(() => {
    if (
      query.forceTour &&
      query.forceTour === "true" &&
      candidatePoolData.data &&
      candidatePoolData.data.length > 0 &&
      !candidatePoolData.loading
    ) {
      closeOnboardingModal();
    }
  }, [query, closeOnboardingModal, candidatePoolData]);

  const PAGE_SIZE = useMemo(() => {
    return candidatePoolData.data?.length || 0;
  }, [candidatePoolData]);

  return (
    <TalentPoolContainer>
      <Modal
        width={500}
        isShown={showModalOnboarding}
        toggle={closeOnboardingModal}
        target={modalTalent}
        handleClose={closeOnboardingModal}
        background={"#F3F3F3"}
        top={"calc(50% - 121px)"}
      >
        <Text
          as="p"
          fontWeight={800}
          color={theme.colors.gray900.main}
          align="center"
        >
          {t("onboarding.modal.title")}
        </Text>
        <Text as="p" color={theme.colors.gray900.main} align="center">
          {t("onboarding.modal.text")}
        </Text>
        <Buttons>
          <Button
            backgroundColor={theme.colors.primaryPurple.main}
            textColor={theme.colors.primaryPurple.text}
            onClick={closeOnboardingModal}
          >
            {t("onboarding.modal.button")}
          </Button>
        </Buttons>
      </Modal>
      <Modal
        width={1000}
        isShown={isShown}
        toggle={handleOpen}
        target={modalTalent}
        handleClose={handleClose}
        background={"#F3F3F3"}
        closeOnEsc={true}
      >
        <Slider
          onNext={() => setIndex(index + 1)}
          onPrev={() => setIndex(index - 1)}
          showNext={index !== PAGE_SIZE - 1 && !candidateDetailData.loading}
          showPrev={index !== 0 && !candidateDetailData.loading}
          disableKeyboard={isJobOfferOpen}
        >
          <Talent
            talentId={
              candidate
                ? candidate?.is_pool && candidate?.user_id
                  ? candidate?.user_id
                  : candidate?.id
                : ""
            }
            userId={candidate?.user_id || ""}
            trackId={candidate?.selection?.source_id}
            ecosystem={query.ecosystem || ""}
            isNotifyOpenCandidateDetail={!candidate?.selection}
            ecosystemTech={""}
            currencyIso={query.currency || ""}
            setIsJobOfferOpen={setIsJobOfferOpen}
          />
        </Slider>
      </Modal>
      <Container>
        <Header
          title={t("private-home-page:tabs.talentPool.title")}
          subtitle={user?.corporate?.name}
          isSelecteds={true}
        />

        <ViewDesktopFilters id="filter">
          <FilterForm
            initialMaxSalary={50000}
            isCandidatePool={true}
            searchCallback={(query) => {
              handleSearch(query);
            }}
            disableKeyboard={isShown}
          />
        </ViewDesktopFilters>
        <Content id="result">
          {candidatePoolData.data && candidatePoolData.data.length > 0 && (
            <>
              <InfoTableContainer>
                <Text
                  fontWeight={600}
                  fontSize={16}
                  margin="0"
                  color={theme.colors.primaryBlue.main}
                >
                  {t("ranking-table.showing-total-talent", {
                    count: candidatePoolData.data?.length || 0,
                  })}
                </Text>
              </InfoTableContainer>

              <Table headerItems={tableHeaderItems}>
                {candidatePoolData.data?.map((c, key) => (
                  <TableRow
                    key={key}
                    onClick={() => {
                      setIndex(key);
                      handleModal(c, candidateStatus);
                    }}
                    id={`candidate-${key}`}
                  >
                    <DataCell>{key + 1}º</DataCell>
                    <DataCell color={theme.colors.primaryBlue.main} bold={600}>
                      {c.initials_name.replace(" ", "").toUpperCase()}
                    </DataCell>
                    <DataCell>{getMainSkills(c, t)}</DataCell>
                    <DataCell>{c.experience_time_tech}</DataCell>
                    <DataCell>
                      {getCandidateSalary(c, t)}{" "}
                      {c.wage_claim_was_converted ? (
                        <TooltipWarning
                          message={getOriginalWageClaim(c)}
                          marginLeft=".5rem"
                        />
                      ) : (
                        <></>
                      )}
                    </DataCell>
                    <DataCell>{getCandidateHireModel(c, t)}</DataCell>
                    <DataCell>{getCandidateHireTypes(c)}</DataCell>
                  </TableRow>
                ))}
              </Table>
            </>
          )}
          {candidatePoolData.data &&
            candidatePoolData.data.length === 0 &&
            Object.keys(query).length > 1 &&
            !candidatePoolData.loading && (
              <EmptyState
                title={t("ranking-table.empty-state.title")}
                description={t("ranking-table.empty-state.description")}
              />
            )}
        </Content>
      </Container>
    </TalentPoolContainer>
  );
};

export default TalentPoolPage;

